import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type ChangeEvent,
} from "react";
import { Settings, X } from "lucide-react";
import { DevSettingsDialog } from "@/components/DevSettingsDialog";
import {
  applyDevSafeZoneCssVars,
  DEV_SAFE_ZONE_DEFAULT_INSET_PX,
  DEV_SAFE_ZONE_INSET_STORAGE_KEY,
  DEV_SAFE_ZONE_PRESET_STORAGE_KEY,
  type DevSafeZonePresetMode,
  isDevSafeZonePreviewEnabled,
  readDevSafeZoneInsetPx,
  readDevSafeZonePresetMode,
  resolveAutoPresetMode,
} from "@/dev/safeZonePreviewEnv";

const MIN = 0;
const MAX = 80;
const DEV_SAFE_ZONE_CONTROLS_COLLAPSED_STORAGE_KEY =
  "um:dev-safe-zone-controls-collapsed";
/** Tab-scoped (sessionStorage): survives refresh, clears when the tab closes. */
const DEV_SAFE_ZONE_CONTROLS_DISMISSED_STORAGE_KEY =
  "um:dev-safe-zone-controls-dismissed";
/** Extra px around the dotted perimeter so the visible line is easy to hit. */
const SAFE_ZONE_RESTORE_SLOP_PX = 12;

const PRESET_OPTIONS: { value: DevSafeZonePresetMode; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "uniform", label: "Custom" },
  { value: "iphone-portrait", label: "iPhone (portrait)" },
  { value: "iphone-landscape", label: "iPhone (landscape)" },
  { value: "ipad-portrait", label: "iPad (portrait)" },
  { value: "ipad-landscape", label: "iPad (landscape)" },
  { value: "android-phone-portrait", label: "Android phone (portrait)" },
  { value: "android-phone-landscape", label: "Android phone (landscape)" },
  { value: "android-tablet-portrait", label: "Android tablet (portrait)" },
  { value: "android-tablet-landscape", label: "Android tablet (landscape)" },
];

const PRESET_LABEL: Partial<Record<DevSafeZonePresetMode, string>> =
  Object.fromEntries(PRESET_OPTIONS.map((o) => [o.value, o.label])) as Partial<
    Record<DevSafeZonePresetMode, string>
  >;

function readControlsDismissed(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.sessionStorage.getItem(DEV_SAFE_ZONE_CONTROLS_DISMISSED_STORAGE_KEY) ===
    "1"
  );
}

function writeControlsDismissed(dismissed: boolean) {
  window.sessionStorage.setItem(
    DEV_SAFE_ZONE_CONTROLS_DISMISSED_STORAGE_KEY,
    dismissed ? "1" : "0"
  );
}

function parseCssPx(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Magenta inset band plus a sliver around the dotted perimeter. */
function isPointInSafeZone(clientX: number, clientY: number): boolean {
  const style = getComputedStyle(document.documentElement);
  const top = parseCssPx(style.getPropertyValue("--safe-area-inset-top"));
  const right = parseCssPx(style.getPropertyValue("--safe-area-inset-right"));
  const bottom = parseCssPx(style.getPropertyValue("--safe-area-inset-bottom"));
  const left = parseCssPx(style.getPropertyValue("--safe-area-inset-left"));
  const w = window.innerWidth;
  const h = window.innerHeight;
  const s = SAFE_ZONE_RESTORE_SLOP_PX;
  return (
    clientY < top + s ||
    clientY > h - bottom - s ||
    clientX < left + s ||
    clientX > w - right - s
  );
}

/**
 * Top preview band; only mounted when dev safe-zone preview is on.
 */
export function DevSafeZonePreviewControls() {
  const viewportBump = useViewportBump();

  const [preset, setPreset] = useState<DevSafeZonePresetMode>(() =>
    typeof window === "undefined" ? "auto" : readDevSafeZonePresetMode()
  );
  const [insetPx, setInsetPx] = useState(() =>
    typeof window === "undefined"
      ? DEV_SAFE_ZONE_DEFAULT_INSET_PX
      : readDevSafeZoneInsetPx()
  );
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.localStorage.getItem(DEV_SAFE_ZONE_CONTROLS_COLLAPSED_STORAGE_KEY) ===
      "1"
    );
  });
  const [isDismissed, setIsDismissed] = useState(readControlsDismissed);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const autoEffective = resolveAutoPresetMode();
  const autoOptionLabel = `Auto (${PRESET_LABEL[autoEffective] ?? autoEffective})`;
  const showUniformSlider =
    preset === "uniform" || (preset === "auto" && autoEffective === "uniform");

  useLayoutEffect(() => {
    applyDevSafeZoneCssVars(preset, insetPx);
    localStorage.setItem(DEV_SAFE_ZONE_PRESET_STORAGE_KEY, preset);
  }, [preset, insetPx, viewportBump]);

  useLayoutEffect(() => {
    if (!showUniformSlider) return;
    localStorage.setItem(DEV_SAFE_ZONE_INSET_STORAGE_KEY, String(insetPx));
  }, [showUniformSlider, insetPx, viewportBump]);

  const dismiss = useCallback(() => {
    writeControlsDismissed(true);
    setIsDismissed(true);
    setIsSettingsOpen(false);
  }, []);

  const restore = useCallback(() => {
    writeControlsDismissed(false);
    setIsDismissed(false);
  }, []);

  useEffect(() => {
    if (!isDismissed) return;
    const onDblClick = (event: MouseEvent) => {
      if (!isPointInSafeZone(event.clientX, event.clientY)) return;
      event.preventDefault();
      restore();
    };
    document.addEventListener("dblclick", onDblClick);
    return () => document.removeEventListener("dblclick", onDblClick);
  }, [isDismissed, restore]);

  const onPresetChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setPreset(e.target.value as DevSafeZonePresetMode);
  }, []);

  const onSliderChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInsetPx(Number(e.target.value));
  }, []);
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(
        DEV_SAFE_ZONE_CONTROLS_COLLAPSED_STORAGE_KEY,
        next ? "1" : "0"
      );
      return next;
    });
  }, []);

  return (
    <>
      {!isDismissed && (
        <div className="pointer-events-none fixed left-0 right-0 top-0 z-[2147483647] flex justify-center px-2 py-1">
          <div
            className="flex max-w-[min(100%,36rem)] flex-nowrap items-center gap-2 rounded-md border border-gray-200/80 bg-white/95 px-2 py-1 text-sm font-medium text-gray-800 shadow-md backdrop-blur-sm pointer-events-auto"
            role="group"
            aria-label="Dev safe zone preview"
          >
            <button
              type="button"
              onClick={toggleCollapsed}
              className="inline-flex min-h-9 shrink-0 items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              aria-expanded={!isCollapsed}
              aria-controls="dev-safe-zone-controls-panel"
              aria-label={
                isCollapsed
                  ? "Expand safe zone controls"
                  : "Collapse safe zone controls"
              }
              title={
                isCollapsed ? "Show safe zone controls" : "Hide safe zone controls"
              }
            >
              <span aria-hidden="true">{isCollapsed ? "▸" : "▾"}</span>
              <span>Safe zone</span>
            </button>

            {!isCollapsed && (
              <div
                id="dev-safe-zone-controls-panel"
                className="flex min-w-0 flex-1 items-center gap-2"
              >
                <select
                  value={preset}
                  onChange={onPresetChange}
                  className="min-h-9 min-w-0 max-w-[min(100%,20rem)] shrink cursor-pointer rounded border border-gray-200 bg-white py-1.5 pl-2 pr-8 text-[16px] font-medium leading-snug text-gray-900"
                  aria-label="Safe area preset"
                >
                  {PRESET_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.value === "auto" ? autoOptionLabel : o.label}
                    </option>
                  ))}
                </select>

                {showUniformSlider && (
                  <>
                    <span
                      className="w-8 shrink-0 border-l border-gray-200 pl-2 text-right tabular-nums text-gray-700"
                      title="Inset all sides"
                    >
                      {insetPx}px
                    </span>
                    <input
                      type="range"
                      min={MIN}
                      max={MAX}
                      value={insetPx}
                      onChange={onSliderChange}
                      className="h-1.5 min-w-[4.5rem] max-w-[7.5rem] flex-1 cursor-pointer accent-fuchsia-600 sm:max-w-[10rem]"
                      aria-label="Simulated safe area inset (all sides)"
                    />
                  </>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => setIsSettingsOpen(true)}
              className="inline-flex min-h-9 w-9 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50"
              aria-haspopup="dialog"
              aria-label="Dev settings"
              title="Dev settings (platform experience, remote config)"
            >
              <Settings size={16} aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={dismiss}
              className="inline-flex min-h-9 w-9 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50"
              aria-label="Dismiss safe zone controls"
              title="Dismiss (double-click the safe zone to restore)"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      <DevSettingsDialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen} />
    </>
  );
}

/**
 * Re-render (and re-run layout effects) when viewport changes — keeps Auto preset + CSS in sync
 * with Chrome device toolbar rotation / responsive preview.
 */
function useViewportBump(): number {
  const [k, setK] = useState(0);
  useLayoutEffect(() => {
    const bump = () => setK((n) => n + 1);
    window.addEventListener("resize", bump);
    window.addEventListener("orientationchange", bump);
    return () => {
      window.removeEventListener("resize", bump);
      window.removeEventListener("orientationchange", bump);
    };
  }, []);
  return k;
}

export function DevSafeZonePreviewControlsGate() {
  if (!isDevSafeZonePreviewEnabled()) return null;
  return <DevSafeZonePreviewControls />;
}
